import React, { forwardRef } from 'react';
import PhoneInput, {
  type Props as PhoneInputProps,
  type DefaultInputComponentProps,
} from 'react-phone-number-input';
import { TextField, type TextFieldProps } from '@mui/material';
import { styled } from '@mui/material/styles';


type MuiPhoneInputProps = TextFieldProps & {
  onChange?: (value: string) => void;
};

const MuiPhoneInput = forwardRef<HTMLInputElement, MuiPhoneInputProps>(
  ({ onChange, ...rest }, ref) => (
    <TextField
      {...rest}
      inputRef={ref}
      onChange={(event) => {
        onChange?.(event.target.value);
      }}
      fullWidth
    />
  ),
);

MuiPhoneInput.displayName = 'MuiPhoneInput';

const StyledPhoneInput = styled(PhoneInput)(({ theme }) => ({
  position: 'relative',
  '.PhoneInputCountry': {
    position: 'absolute',
    left: 12,
    top: 28,
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    pointerEvents: 'none',
  },
  '.PhoneInputCountrySelect': {
    width: 0,
    height: 0,
    border: 'none',
    padding: 0,
    margin: 0,
    opacity: 0,
  },
  '.PhoneInputCountryIcon': {
    width: 32,
    height: 20,
    borderRadius: 3,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    boxShadow: theme.shadows[1],
    border: `1px solid ${theme.palette.divider}`,
  },
  '.PhoneInputInput': {
    '& .MuiOutlinedInput-input': {
      marginLeft: 40,
    }
  } 
}));

export interface PhoneNumberFieldProps
  extends PhoneInputProps<DefaultInputComponentProps> {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  error?: boolean;
}

export default function PhoneNumberField({
  label,
  helperText,
  error,
  ...props
}: PhoneNumberFieldProps) {
  const {
    defaultCountry = 'BR',
    international = true,
    ...phoneInputProps
  } = props;

  return (
    <StyledPhoneInput
      {...phoneInputProps}
      international={international}
      defaultCountry={defaultCountry}
      inputComponent={MuiPhoneInput}
      label={label}
      helperText={helperText}
      error={error}
      variant="outlined"
      fullWidth
    />
  );
}
